import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';
import { initPostHog } from './lib/posthog';
import { installDomGuards } from './lib/dom-guards';

installDomGuards();

// Initialize Sentry (before anything else)
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  initialScope: {
    tags: { site: 'frontend' },
  },
  ignoreErrors: [
    // Facebook/Instagram in-app browser (Android) injected scripts losing
    // their native bridge — not our code
    /Java object is gone/,
    // Stale hashed chunks after a redeploy / flaky mobile network — recovered
    // by the vite:preloadError reload in dom-guards.ts
    /Failed to fetch dynamically imported module/,
    /Importing a module script failed/,
  ],
  denyUrls: [
    // Facebook/Instagram in-app browser injected scripts (iabjs://...)
    /^iabjs:\/\//,
    // PostHog session recorder network failures
    /posthog-recorder\.js/,
  ],
});

// Initialize PostHog analytics
initPostHog();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>A apărut o eroare neașteptată</h1>
          <p>Te rugăm să reîncarci pagina.</p>
          <button onClick={() => window.location.reload()}>Reîncarcă</button>
        </div>
      }
    >
      <HelmetProvider>
        <GoogleReCaptchaProvider
          reCaptchaKey={recaptchaSiteKey}
          scriptProps={{
            async: true,
            defer: true,
            appendTo: 'head',
          }}
        >
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </QueryClientProvider>
        </GoogleReCaptchaProvider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
